import { RenderTypeEnum, MainOptionsEnum, OutputType } from '@internal/console/atom';

const workHistory = [
  {
    company: "Compass",
    title: "Software Engineer",
    achievements: [
      "Led a team to build an automation system for installing prerelease packages into services and deployed into testing instances.",
      "Collaborated on design, and fully implemented the package upkeep system through greenkeeper automation and code change enforcement. Utilized datadog metrics to bring visibility of lack of upkeep to all frontend engineers.",
      "Restructured the CI pipeline into a tag based release system that coordinated with the release service and CI scripts.",
      "Designed and implemented a solution to by-pass an error prone service by extending the in-house thrift-typescript generator and implementing the internal token package for JWT validation.",
      "Implemented the package auto-publishing service that removed the manual publishing process completely through the use of conventional commits.",
      "Supported and guided all compass engineers in best practices utilizing the infrastructure.",
    ],
  },
  {
    company: "Spotify",
    title: "Software Engineer",
    achievements: [
      "Planned and delivered the Feature UI redesign along with a refactor to reduce latency by 83% (3s to 500ms)",
      "Assumed responsibilites for the system design and led the team to a successful delivery of feature versions.",
      "Designed and implemented the kubernetes interface for our cloud development environment.",
    ],
  },
];

type SelectionType = {
  [k in MainOptionsEnum.AboutMe
    | MainOptionsEnum.Resume
    | MainOptionsEnum.Skills
    | MainOptionsEnum.Projects
    | MainOptionsEnum.Handball
    | MainOptionsEnum.Investments
    | MainOptionsEnum.RealEstate
    | MainOptionsEnum.GoBack]: OutputType[]
};

export const selectionOutput: SelectionType = {
  [MainOptionsEnum.AboutMe]: [
    { type: RenderTypeEnum.Typing, text: "user> You've selected the option \"About Me\"" },
    { type: RenderTypeEnum.Typing, text: "Thanks for looking to learn more about me!" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "I'm a machine learning engineer with a passion for solving cool problems." },
    { type: RenderTypeEnum.Typing, text: "Currently I'm at Spotify on the Machine Learning Platform working on machine learning operations and distributed compute!" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "These are the other option(s):" },
    {
      type: RenderTypeEnum.InputOptions, options: [
        { label: "Resume", value: MainOptionsEnum.Resume },
        { label: "Skills", value: MainOptionsEnum.Skills },
        { label: "Projects", value: MainOptionsEnum.Projects },
      ]
    },
  ],
  [MainOptionsEnum.Resume]: [
    { type: RenderTypeEnum.Typing, text: "user> You've selected the option \"Resume\"" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "I'm going to open a resume in a new tab..." },
    { type: RenderTypeEnum.OpenLink, url: "https://drive.google.com/file/d/0Bx2FPcZ6XMamQTdTMEhnVlZjb1E/view?usp=drive_link&resourcekey=0-HcP4tv1cSP2luWyEj3FHVg" },
    { type: RenderTypeEnum.Append, text: "If your browser is blocking pop-ups please enable pop-ups for calvinlee.dev. In the meantime here's the link:" },
    { type: RenderTypeEnum.TextLink, text: "resume link", url: "https://drive.google.com/file/d/0Bx2FPcZ6XMamQTdTMEhnVlZjb1E/view" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "These are the other option(s):" },
    {
      type: RenderTypeEnum.InputOptions, options: [
        { label: "About Me", value: MainOptionsEnum.AboutMe },
        { label: "Skills", value: MainOptionsEnum.Skills },
        { label: "Projects", value: MainOptionsEnum.Projects },
      ]
    },
  ],
  [MainOptionsEnum.Skills]: [
    { type: RenderTypeEnum.Typing, text: "user> You've selected the option \"Skills\"" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "Where should I begin?" },
    { type: RenderTypeEnum.Typing, text: "I have built UIs for analytics, experimentation tracking, and feature discovery. Designed and implemented backend microservices to support clients." },
    { type: RenderTypeEnum.Typing, text: "Dabbled with data engineering for the feature store. Dived deep into Kubernetes for building out the VSCode workbench." },
    { type: RenderTypeEnum.Typing, text: "I'm just a generalist :)" },
    { type: RenderTypeEnum.Typing, text: "My latest journey has me building out our Machine Learning Platform for generative recommenders and our best practices!" },
    { type: RenderTypeEnum.Typing, text: "For more details, take a look at my resume!" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "These are the other option(s):" },
    {
      type: RenderTypeEnum.InputOptions, options: [
        { label: "About Me", value: MainOptionsEnum.AboutMe },
        { label: "Resume", value: MainOptionsEnum.Resume },
        { label: "Projects", value: MainOptionsEnum.Projects },
      ]
    },
  ],
  [MainOptionsEnum.Projects]: [
    { type: RenderTypeEnum.Typing, text: "user> You've selected the option \"Projects\"" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "These are the projects you can explore right now:" },
    {
      type: RenderTypeEnum.InputOptions, options: [
        { label: "Handball", value: MainOptionsEnum.Handball },
        { label: "Investments", value: MainOptionsEnum.Investments },
        { label: "Real Estate", value: MainOptionsEnum.RealEstate },
        { label: "Go Back", value: MainOptionsEnum.GoBack },
      ]
    },
  ],
  [MainOptionsEnum.Handball]: [
    { type: RenderTypeEnum.Typing, text: "user> You've selected the option \"Handball\"" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "Opening the Handball page..." },
    { type: RenderTypeEnum.OpenLink, url: "/#/handball", target: "_self" },
  ],
  [MainOptionsEnum.Investments]: [
    { type: RenderTypeEnum.Typing, text: "user> You've selected the option \"Investments\"" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "Opening the Investments page..." },
    { type: RenderTypeEnum.OpenLink, url: "/#/investments", target: "_self" },
  ],
  [MainOptionsEnum.RealEstate]: [
    { type: RenderTypeEnum.Typing, text: "user> You've selected the option \"Real Estate\"" },
    { type: RenderTypeEnum.Append, text: "---" },
    { type: RenderTypeEnum.Typing, text: "Opening the Real Estate ROI lab..." },
    { type: RenderTypeEnum.OpenLink, url: "/#/real-estate/intake", target: "_self" },
  ],
  [MainOptionsEnum.GoBack]: [
    { type: RenderTypeEnum.OpenLink, url: "/#/", target: "_self" },
  ],
};
